import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge, fmtDateTime,
} from "../../components/app/Kit";

const FLOW = ["New lead", "Create CRM record", "Qualify", "Follow up", "Book appointment", "Confirm", "Remind", "Complete", "Ask for review"];

const EngineStatus = ({ onRan }) => {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/automation-engine/status");
      setStatus(data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/automations/run-now");
      toast.success(
        data.runs_created > 0
          ? `Engine executed ${data.runs_created} action(s) across ${data.per_automation.length} automation(s).`
          : "Engine ran — everything is already up to date, no new actions were needed."
      );
      await load();
      onRan?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Panel
      testId="engine-status-panel"
      title="Automation engine"
      className="mb-4"
      action={
        <button
          type="button"
          data-testid="engine-run-now-btn"
          onClick={runNow}
          disabled={running}
          className="inline-flex items-center gap-1.5 rounded-md bg-elv-green px-3.5 py-1.5 text-[11px] font-semibold text-[#04120A] disabled:opacity-60"
        >
          {running ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Play className="h-3 w-3" />}
          {running ? "Running…" : "Run engine now"}
        </button>
      }
    >
      <p className="text-[11px] text-elv-dim">
        Enabled workflows are evaluated against your live data on a {status?.cadence_minutes ?? 15}-minute schedule.
        Actions that need an SMS or email provider are logged as{" "}
        <span className="text-amber-200">skipped — provider not connected</span> until one is connected in Integrations.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-elv-dim" data-testid="engine-status-meta">
        <Pill tone={status?.last_run_at ? "green" : "dim"}>
          {status?.last_run_at ? "engine active" : "awaiting first run"}
        </Pill>
        <span className="font-mono">
          Last run: {status?.last_run_at ? fmtDateTime(status.last_run_at) : "never"}
        </span>
        {status?.last_run_at ? (
          <>
            <span>Trigger: {status.last_trigger}</span>
            <span>Actions last run: {status.last_runs_created}</span>
            <span>Workflows evaluated: {status.automations_evaluated}</span>
          </>
        ) : null}
      </div>
    </Panel>
  );
};

export default function Automations() {
  const { data, state, error, reload, setData } = useResource("/automations");
  const [expanded, setExpanded] = useState(null);
  const [runs, setRuns] = useState({});

  const toggle = async (a) => {
    try {
      const { data: updated } = await api.post(`/automations/${a.id}/toggle`);
      setData((list) => list.map((x) => (x.id === a.id ? { ...x, ...updated } : x)));
      toast.success(`${a.name} ${updated.enabled ? "enabled" : "paused"}.`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openHistory = async (a) => {
    const next = expanded === a.id ? null : a.id;
    setExpanded(next);
    if (next && !runs[a.id]) {
      try {
        const { data: r } = await api.get(`/automation-runs/${a.id}`);
        setRuns((prev) => ({ ...prev, [a.id]: r }));
      } catch (e) {
        setRuns((prev) => ({ ...prev, [a.id]: [] }));
      }
    }
  };

  return (
    <div data-testid="automations-page">
      <PageTitle
        testId="automations-title"
        title="Turn repetitive work into automatic workflows."
        sub="Each automation has a trigger, a set of actions, a delay and a full execution history."
        action={<DemoBadge />}
      />

      <EngineStatus onRan={() => { setRuns({}); reload(); }} />

      <Panel testId="automation-flow" title="Standard lead-to-review workflow" className="mb-4">
        <div className="scrollbar-thin overflow-x-auto">
          <div className="flex min-w-max items-center gap-2">
            {FLOW.map((step, i, arr) => (
              <div key={step} className="flex items-center gap-2">
                <span
                  data-testid={`automation-flow-node-${i}`}
                  className="rounded-md border border-elv-green/20 bg-elv-green/[0.06] px-3 py-2 text-[11px] uppercase tracking-[0.1em] text-elv-text"
                >
                  {step}
                </span>
                {i < arr.length - 1 ? <Icons.ChevronRight className="h-3.5 w-3.5 text-elv-green/70" /> : null}
              </div>
            ))}
          </div>
        </div>
      </Panel>

      {state === "loading" ? <Loading rows={8} testId="automations-loading" /> : null}
      {state === "error" ? <ErrorState message={error} onRetry={reload} testId="automations-error" /> : null}
      {state === "ready" && data.length === 0 ? (
        <EmptyState icon="Workflow" title="No automations yet" sub="Automations are configured during onboarding, then refined as you go." testId="automations-empty" />
      ) : null}

      {state === "ready" && data.length > 0 ? (
        <div className="grid gap-2 lg:grid-cols-2">
          {data.map((a) => (
            <div key={a.id} data-testid={`automation-${a.id}`} className="glass rounded-lg p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-elv-text">{a.name}</p>
                  <p className="mt-1.5 text-[11px] text-elv-dim">Trigger: {a.trigger}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={a.enabled}
                  onClick={() => toggle(a)}
                  data-testid={`automation-toggle-${a.id}`}
                  className={`relative h-5 w-9 shrink-0 rounded-full border transition-colors duration-300 ${
                    a.enabled ? "border-elv-green/50 bg-elv-green/30" : "border-white/10 bg-white/5"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-3.5 w-3.5 rounded-full transition-transform duration-300 ${
                      a.enabled ? "translate-x-[18px] bg-elv-green" : "translate-x-0.5 bg-white/40"
                    }`}
                  />
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {a.actions.map((act) => (
                  <span key={act} className="rounded-sm border border-white/[0.08] bg-white/[0.03] px-2 py-0.5 text-[10px] text-elv-dim">
                    {act}
                  </span>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-elv-dim">
                <Pill tone={a.enabled ? "green" : "dim"}>{a.status}</Pill>
                <span>Delay: {a.delay}</span>
                <span>Runs: {a.runs_total}</span>
                <span className="font-mono">Last: {fmtDateTime(a.last_run)}</span>
              </div>

              <button
                type="button"
                onClick={() => openHistory(a)}
                data-testid={`automation-history-toggle-${a.id}`}
                className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-elv-green hover:text-elv-cyan"
              >
                {expanded === a.id ? "Hide" : "View"} execution history
                <Icons.ChevronDown className={`h-3 w-3 transition-transform duration-300 ${expanded === a.id ? "rotate-180" : ""}`} />
              </button>

              {expanded === a.id ? (
                <div data-testid={`automation-history-${a.id}`} className="mt-3 grid gap-1.5 border-t border-elv-line pt-3">
                  {(runs[a.id] || []).length === 0 ? (
                    <p className="text-[11px] text-elv-dim">No executions recorded yet.</p>
                  ) : (
                    (runs[a.id] || []).map((r) => (
                      <div key={r.id} className="flex items-center justify-between gap-3 text-[11px]">
                        <span className="text-elv-dim">{r.detail}</span>
                        <span className="flex items-center gap-2">
                          <Pill tone={r.result === "success" ? "green" : "dim"}>{r.result}</Pill>
                          <span className="font-mono text-[10px] text-elv-dim">{fmtDateTime(r.created_at)}</span>
                        </span>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
